import React, { Component } from 'react';
import ReactDOM from 'react-dom';

// Components
import Resizer from 'components/ResizablePanels/Resizer';
import './styles.css';

export const MIN_COUNTER_PANEL_WIDTH = 200;

/** Ancho guardado por un arrastre real. También vale por debajo del mínimo automático. */
export function hasManualCounterWidth(storedWidth) {
  const manual = Number(storedWidth);
  return Number.isFinite(manual) && manual > 1;
}

/**
 * Ancho del panel de contadores tras una medición del contenido.
 * El ancho guardado por el usuario se mantiene al cambiar de fechas.
 * Un panel cerrado no se reabre.
 *
 * @returns {string|null} nuevo tamaño del panel (p. ej. "222px"), o null si no cambia
 */
export function resolveCounterPanelSize({
  isOpen,
  currentSize,
  contentWidth,
  storedWidth,
  containerLimit,
}) {
  if (!isOpen) return null;

  const currentPx =
    typeof currentSize === "string" && currentSize.endsWith("px")
      ? parseFloat(currentSize)
      : currentSize === 0
        ? 0
        : null;

  // Arrastre hasta el borde: queda cerrado hasta que el usuario lo abra.
  if (currentSize === "0" || currentSize === 0 || currentPx === 0) return null;

  const manual = Number(storedWidth);
  const hasManual = hasManualCounterWidth(manual);

  let target;
  if (hasManual) {
    target = manual;
    if (Number.isFinite(containerLimit) && containerLimit >= MIN_COUNTER_PANEL_WIDTH) {
      target = Math.min(target, containerLimit);
    }
  } else if (Number.isFinite(contentWidth) && contentWidth > 0) {
    target = contentWidth;
  } else {
    return null;
  }

  const next = `${Math.round(target)}px`;
  return currentSize === next ? null : next;
}

export default class ResizablePanels extends Component {
  constructor(props) {
    super(props);

    this.resizable = React.createRef();
    this.rafId = null;
    this.resizeObserver = null;
    this.hasResized = false; // Flag para detectar si se hizo resize antes del click

    // Si está en modo toggle, inicializar el estado desde localStorage
    const isToggleMode = this.props.toggleMode !== false; // Por defecto true si no se especifica
    const storedToggleState = isToggleMode ? localStorage.getItem("plan_panel_toggle") : null;
    const isOpen = storedToggleState !== null ? storedToggleState === 'true' : true; // Por defecto abierto

    this.state = {
      panelsSize: [],
      resizing: false,
      isToggleMode: isToggleMode,
      isOpen: isOpen,
      maxCounterWidth: null, // Ancho máximo calculado para el contador
      lastIntrinsicWidth: null // Ancho real (scrollWidth) de la tabla de contadores
    };
  }


  getStorageKey() {
    return this.props.storageKey || 'plan_panel_size';
  }

  notifyPanelsLayoutChange = () => {
    if (typeof this.props.onPanelsLayoutChange === 'function') {
      this.props.onPanelsLayoutChange();
    }
  };

  componentDidMount() {
    const { isToggleMode, isOpen } = this.state;

    // Siempre agregar event listeners para resize (también funciona en modo toggle cuando está abierto)
    document.addEventListener('mousemove', this.executeResize, { passive: true });
    document.addEventListener('mouseup', this.stopResize);
    // No usar mouseleave: al arrastrar el splitter vertical hacia el borde
    // inferior el cursor puede salir del viewport y “trabar” el drag.

    if (isToggleMode) {
      // Siempre arrancar con 'auto' para que el panel se ajuste al contenido real.
      // El ancho guardado en localStorage se usa solo como tope máximo (si el
      // usuario lo había achicado manualmente) una vez que se mide el contenido.
      this.updatePanelsSizeForToggle(isOpen);
      if (isOpen) {
        setTimeout(() => this.calculateCounterWidth(), 100);
      }
    } else {
      // Modo resizable: comportamiento original
      var storedPanelsSize = localStorage.getItem(this.getStorageKey());
      var panelsSize = this.props.panelsSize;

      if (this.props.isMax)
        panelsSize = [100, 0];
      else if (storedPanelsSize) {
        storedPanelsSize = storedPanelsSize.split(",");
        storedPanelsSize = [parseFloat(storedPanelsSize[0]), parseFloat(storedPanelsSize[1])];
        panelsSize = this.clampPanelsSize(storedPanelsSize);
      } else {
        panelsSize = this.clampPanelsSize(panelsSize);
      }

      this.setState({ ...this.state, panelsSize }, () => {
        // Re-clamp cuando el contenedor ya tiene tamaño real (mínimos/máximos en px)
        if (this.props.minPanelsSizePx || this.props.maxPanelsSizePx) {
          requestAnimationFrame(() => {
            const clamped = this.clampPanelsSize(this.state.panelsSize);
            if (
              clamped[0] !== this.state.panelsSize[0] ||
              clamped[1] !== this.state.panelsSize[1]
            ) {
              this.setState({ panelsSize: clamped });
            }
          });
        }
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { isToggleMode, isOpen } = this.state;

    if (isToggleMode) {
      // Si la vista cambia, el conjunto de columnas es otro: olvidar el ancho
      // manual y volver a ajustar al contenido. Cambiar de fechas no entra aquí.
      if (this.props.viewId !== undefined && prevProps.viewId !== this.props.viewId) {
        if (isOpen) {
          localStorage.removeItem("plan_counter_width");
          this.setState({
            panelsSize: ['flex', 'auto'],
            maxCounterWidth: null
          }, () => {
            setTimeout(() => this.calculateCounterWidth(), 100);
          });
        }
      }

      // En modo toggle, manejar cambios en isMax
      if (prevProps.isMax !== this.props.isMax) {
        const isOpen = !this.props.isMax;
        this.setState({ isOpen });
        this.updatePanelsSizeForToggle(isOpen);
        // Recalcular el ancho cuando se abre/cierra
        if (isOpen) {
          setTimeout(() => this.calculateCounterWidth(), 0);
        }
      }
      // Recalcular el ancho si el panel está abierto y los children han cambiado
      if (isOpen && prevProps.children !== this.props.children) {
        setTimeout(() => this.calculateCounterWidth(), 100);
      }
    } else {
      // Modo resizable: comportamiento original
      if (prevProps.isMax !== this.props.isMax) {
        var storedPanelsSize = localStorage.getItem(this.getStorageKey());
        var panelsSize;

        if (this.props.isMax) {
          panelsSize = [100, 0];
        } else if (storedPanelsSize) {
          storedPanelsSize = storedPanelsSize.split(",");
          panelsSize = this.clampPanelsSize([
            parseFloat(storedPanelsSize[0]),
            parseFloat(storedPanelsSize[1]),
          ]);
        } else {
          panelsSize = this.clampPanelsSize(this.props.panelsSize);
        }

        this.setState({ ...this.state, panelsSize });
      } else if (
        !this._resizing &&
        (this._pxLimitChanged(prevProps.minPanelsSizePx, this.props.minPanelsSizePx) ||
          this._pxLimitChanged(prevProps.maxPanelsSizePx, this.props.maxPanelsSizePx))
      ) {
        // Recalcular cuando cambia el tope (p. ej. alto de la tabla de necesidades)
        const clamped = this.clampPanelsSize(this.state.panelsSize);
        if (
          !this.state.panelsSize ||
          clamped[0] !== this.state.panelsSize[0] ||
          clamped[1] !== this.state.panelsSize[1]
        ) {
          this.setState({ panelsSize: clamped });
        }
      }
    }
  }

  _pxLimitChanged = (prev, next) => {
    if (prev === next) return false;
    if (!prev || !next) return prev !== next;
    return Number(prev[0]) !== Number(next[0]) || Number(prev[1]) !== Number(next[1]);
  };

  /**
   * Aplica mínimos/máximos por panel (min/maxPanelsSize en unidad actual y/o px)
   * y recalcula el panel hermano para que la suma se mantenga.
   */
  clampPanelsSize = (panelsSize) => {
    if (!panelsSize || panelsSize.length < 2) return panelsSize;

    const minPanelsSize = this.props.minPanelsSize;
    const minPanelsSizePx = this.props.minPanelsSizePx;
    const maxPanelsSize = this.props.maxPanelsSize;
    const maxPanelsSizePx = this.props.maxPanelsSizePx;
    if (!minPanelsSize && !minPanelsSizePx && !maxPanelsSize && !maxPanelsSizePx) {
      return panelsSize;
    }

    const usePercentage = this.props.sizeUnitMeasure === '%';
    const resizerSize = parseFloat(this.props.resizerSize) || 10;
    const resizerCount = Math.max((panelsSize.length || 2) - 1, 1);
    const containerSizePx = this.resizable?.current
      ? this.getCurrentComponentSize()
      : 0;
    // El espacio repartible excluye el/los resizers; si no, top+bottom=100%
    // + resizer desborda el contenedor y el splitter se “traba” al fondo.
    const availablePx = containerSizePx > 0
      ? Math.max(0, containerSizePx - resizerSize * resizerCount)
      : 0;
    let total = usePercentage
      ? 100
      : (availablePx || null);
    if (!Number.isFinite(total) || total <= 0) {
      total = panelsSize.reduce((sum, size) => sum + Number(size), 0);
    }
    if (!Number.isFinite(total) || total <= 0) return panelsSize;

    const toUnit = (px) => {
      if (!Number.isFinite(px) || px <= 0 || availablePx <= 0) return NaN;
      return usePercentage ? (px * 100) / availablePx : px;
    };

    const resolvedMins = panelsSize.map((_, index) => {
      const unitMin = minPanelsSize ? Number(minPanelsSize[index]) : NaN;
      const pxMin = minPanelsSizePx ? Number(minPanelsSizePx[index]) : NaN;
      let min = Number.isFinite(unitMin) ? unitMin : 0;
      const asUnit = toUnit(pxMin);
      if (Number.isFinite(asUnit)) min = Math.max(min, asUnit);
      return min > 0 ? min : NaN;
    });

    const resolvedMaxs = panelsSize.map((_, index) => {
      const unitMax = maxPanelsSize ? Number(maxPanelsSize[index]) : NaN;
      const pxMax = maxPanelsSizePx ? Number(maxPanelsSizePx[index]) : NaN;
      let max = Number.isFinite(unitMax) ? unitMax : Infinity;
      const asUnit = toUnit(pxMax);
      if (Number.isFinite(asUnit)) max = Math.min(max, asUnit);
      return Number.isFinite(max) && max < Infinity ? max : NaN;
    });

    let next = panelsSize.map((size) => Number(size));

    next = next.map((size, index) => {
      let s = size;
      const min = resolvedMins[index];
      const max = resolvedMaxs[index];
      if (Number.isFinite(min)) s = Math.max(s, min);
      if (Number.isFinite(max)) s = Math.min(s, max);
      // Si min > max (tabla más baja que el mínimo pedido), gana el max
      if (Number.isFinite(min) && Number.isFinite(max) && min > max) {
        s = max;
      }
      return s;
    });

    const min0 = Number.isFinite(resolvedMins[0]) ? resolvedMins[0] : 0;
    const min1 = Number.isFinite(resolvedMins[1]) ? resolvedMins[1] : 0;
    const max0 = Number.isFinite(resolvedMaxs[0]) ? resolvedMaxs[0] : Infinity;
    const max1 = Number.isFinite(resolvedMaxs[1]) ? resolvedMaxs[1] : Infinity;
    const sum = next[0] + next[1];

    // Si ambos mínimos no caben, priorizar el panel inferior/derecho (necesidades).
    // Si solo hay overshoot (float o drag), conservar el tamaño pedido y
    // repartir el resto — NUNCA forzar el panel inferior a su mínimo
    // (eso dejaba el splitter “preso” al intentar agrandar cobertura).
    if (sum > total + 1e-6) {
      if (min0 + min1 > total + 1e-6) {
        next[1] = Math.min(min1, max1, total);
        next[0] = Math.max(0, total - next[1]);
      } else {
        next[1] = Math.min(Math.max(next[1], min1), Math.min(max1, total - min0));
        next[0] = total - next[1];
        if (next[0] < min0) {
          next[0] = min0;
          next[1] = Math.max(0, Math.min(max1, total - next[0]));
        }
        if (next[0] > max0) {
          next[0] = max0;
          next[1] = Math.max(min1, Math.min(max1, total - next[0]));
        }
      }
    } else if (Math.abs(sum - total) > 1e-6) {
      // Sobra espacio (p. ej. inferior topeado por max): se lo come el superior
      next[0] = total - next[1];
      if (next[0] < min0) {
        next[0] = min0;
        next[1] = Math.max(min1, Math.min(max1, total - next[0]));
      }
      if (next[0] > max0) {
        next[0] = max0;
        next[1] = Math.max(min1, Math.min(max1, total - next[0]));
      }
    }

    return next;
  };

  calculateCounterWidth = () => {
    const { isToggleMode, isOpen } = this.state;
    if (!isToggleMode || !isOpen) return;

    // Usar requestAnimationFrame para asegurar que el DOM esté actualizado
    requestAnimationFrame(() => {
      // Buscar la tabla de contadores en el segundo panel (índice 1)
      const counterPanel = this.resizable.current?.querySelectorAll('.resizable-fragment')[1];
      if (!counterPanel) return;

      const tableCounters = counterPanel.querySelector('#tableCounters');
      if (!tableCounters) return;

      // Configurar ResizeObserver para observar cambios en el tamaño de la tabla
      if (!this.resizeObserver && typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateCounterWidth(tableCounters);
        });
        this.resizeObserver.observe(tableCounters);
      }

      if (!this.state.isOpen) return;

      // Calcular el ancho inicial
      this.updateCounterWidth(tableCounters);
    });
  }

  updateCounterWidth = (tableCounters) => {
    if (!tableCounters || this.state.resizing || !this.state.isOpen) return;

    // Calcular el ancho real de la tabla incluyendo todas las columnas
    // Temporalmente quitamos el ancho forzado para medir un ancho "intrínseco" real sin estirar
    const originalWidth = tableCounters.style.width;
    tableCounters.style.width = 'auto'; // Forzamos a que se ajuste a su contenido
    const intrinsicWidth = tableCounters.scrollWidth;
    tableCounters.style.width = originalWidth; // Restauramos

    if (intrinsicWidth > 0) {
      const contentWidth = Math.max(intrinsicWidth + 2, MIN_COUNTER_PANEL_WIDTH);
      const storedWidth = parseFloat(localStorage.getItem("plan_counter_width") || "0");
      let containerLimit = null;
      if (this.resizable?.current) {
        const containerWidth = this.getCurrentComponentSize();
        const resizerWidth = parseFloat(this.props.resizerSize) || 10;
        if (containerWidth > 0) {
          containerLimit = Math.max(0, containerWidth - resizerWidth);
        }
      }

      this._counterWidthChanged = false;
      this.setState(prevState => {
        const nextSize = resolveCounterPanelSize({
          isOpen: prevState.isOpen,
          currentSize: prevState.panelsSize?.[1],
          contentWidth,
          storedWidth,
          containerLimit,
        });
        const intrinsicSame = prevState.lastIntrinsicWidth === intrinsicWidth;
        if (nextSize == null) {
          if (intrinsicSame) return null;
          return { lastIntrinsicWidth: intrinsicWidth };
        }
        const panelsSize = [...(prevState.panelsSize || [])];
        panelsSize[1] = nextSize;
        this._counterWidthChanged = true;
        return { panelsSize, lastIntrinsicWidth: intrinsicWidth };
      }, () => {
        if (this._counterWidthChanged) {
          this._counterWidthChanged = false;
          this.notifyPanelsLayoutChange();
        }
      });
    }
  }

  updatePanelsSizeForToggle = (isOpen) => {
    if (isOpen) {
      // Si el usuario ya eligió un ancho, usarlo desde el primer pintado.
      // 'auto' solo cuando no hay preferencia, para medir el contenido.
      const storedWidth = parseFloat(localStorage.getItem("plan_counter_width") || "0");
      const hasManual = hasManualCounterWidth(storedWidth);
      const panelsSize = hasManual
        ? ['flex', `${Math.round(storedWidth)}px`]
        : ['flex', 'auto'];
      this.setState({ panelsSize }, () => {
        // Calcular el ancho real después de que el estado se actualice
        setTimeout(() => this.calculateCounterWidth(), 100);
        this.notifyPanelsLayoutChange();
      });
    } else {
      // Cuando está cerrado, el counter está oculto
      this.setState({ panelsSize: ['100%', '0'] }, () => {
        this.notifyPanelsLayoutChange();
      });
    }
  }

  handleToggle = () => {
    if (this._resizing) {
      this.stopResize();
    }
    // Solo hacer toggle si no se hizo resize (si el usuario solo hizo click sin arrastrar)
    if (this.hasResized) {
      this.hasResized = false;
      return;
    }

    const { isOpen } = this.state;
    const newIsOpen = !isOpen;

    this.setState({ isOpen: newIsOpen });
    localStorage.setItem("plan_panel_toggle", newIsOpen.toString());
    if (!newIsOpen) {
      this._panelsSize = null;
    }
    this.updatePanelsSizeForToggle(newIsOpen);
  }

  componentWillUnmount() {
    this.stopResize();
    // Limpiar event listeners y cancelar animationFrame
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    document.removeEventListener('mousemove', this.executeResize);
    document.removeEventListener('mouseup', this.stopResize);

    // Limpiar ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  render() {
    const { bkcolor } = this.props;
    const rest =
      this.props.children.length > 1 ? this.props.children.slice(1) : [];

    return (
      <div
        style={{
          width: this.props.width,
          height: this.props.height,
          background: bkcolor,
          display: 'flex',
          flexDirection: this.props.displayDirection || 'row'
        }}
        ref={this.resizable}
        className={`resizable-panels ${this.props.className || ''} ${this.state.resizing ? 'resizing' : ''}`.trim()}
      >
        {this.renderFirst()}
        {this.renderRest(rest)}
      </div>

    );
  }

  renderFirst() {
    return this.renderChildren(this.props.children[0], 0);
  }

  renderRest(rest) {
    return [].concat(
      ...rest.map((children, index) => {
        return [
          this.renderResizer(index + 1),
          this.renderChildren(children, index + 1)
        ];
      })
    );
  }

  renderChildren(children, index) {
    return (
      <div
        className="resizable-fragment"
        key={`fragment_` + index}
        style={this.getStyle(index)}
      >
        {children}
      </div>
    );
  }

  renderResizer(index) {
    const { isToggleMode, isOpen } = this.state;

    return (
      <Resizer
        size={this.props.resizerSize}
        key={`resizer_` + index}
        direction={this.props.displayDirection}
        onMouseDown={isToggleMode && isOpen ? (e => this.startResize(e, index)) : (isToggleMode ? null : (e => this.startResize(e, index)))}
        onClick={isToggleMode ? this.handleToggle : null}
        color={this.props.resizerColor}
        isToggleMode={isToggleMode}
        isOpen={isOpen}
      />
    );
  }

  displayDirectionIsColumn() {
    return this.props.displayDirection === 'column' ? true : false;
  }

  getStyle(index) {
    const { isToggleMode, isOpen } = this.state;
    const panelsSize = this.state.panelsSize || [];
    const panelsSizeLength = panelsSize.length - 1;
    const size = index > panelsSizeLength ? '100%' : panelsSize[index];
    const unitMeasure = this.props.sizeUnitMeasure || 'px';

    if (this.displayDirectionIsColumn()) {
      if (isToggleMode && size === 'auto') {
        return {
          height: 'auto',
          width: `100%`,
          overflow: 'hidden'
        };
      }

      // En columna + % usamos flexGrow (no height%). Así el resizer fijo
      // (10px) no provoca overflow: top% + resizer + bottom% > 100%.
      // Ese overflow dejaba el panel atrapado al bajar el splitter con
      // pocos empleados.
      if (unitMeasure === '%' || this.props.sizeUnitMeasure === '%') {
        const minPx = this.props.minPanelsSizePx
          ? Number(this.props.minPanelsSizePx[index])
          : 0;
        const maxPx = this.props.maxPanelsSizePx
          ? Number(this.props.maxPanelsSizePx[index])
          : 0;
        return {
          flexGrow: Number(size) || 0,
          flexShrink: 1,
          flexBasis: 0,
          width: '100%',
          overflow: 'hidden',
          minHeight: Number.isFinite(minPx) && minPx > 0 ? minPx : 0,
          maxHeight: Number.isFinite(maxPx) && maxPx > 0 ? maxPx : undefined,
        };
      }

      return {
        height: `${size}${unitMeasure}`,
        width: `100%`,
        overflow: 'hidden',
        flexShrink: 0,
        flexGrow: 0,
        minHeight: 0
      };
    }

    // Para modo row (horizontal)
    if (isToggleMode) {
      if (size === 'auto') {
        // Panel con tamaño automático (counter cuando está abierto)
        // Se calculará el ancho real en calculateCounterWidth
        return {
          height: `100%`,
          width: 'auto',
          minWidth: 0,
          flexShrink: 0,
          flexGrow: 0,
          overflow: 'hidden'
        };
      } else if (typeof size === 'string' && size.endsWith('px')) {
        // Panel con ancho calculado en píxeles (counter después de calcular)
        return {
          height: `100%`,
          width: size,
          minWidth: 0,
          flexShrink: 0,
          flexGrow: 0,
          overflow: 'hidden'
        };
      } else if (size === 'flex') {
        // Panel principal (plan) cuando está abierto - toma el resto del espacio
        return {
          height: `100%`,
          flex: '1 1 auto',
          minWidth: 0,
          overflow: 'hidden'
        };
      } else if (size === '0' || size === 0) {
        // Panel oculto (counter cuando está cerrado)
        return {
          height: `100%`,
          width: '0',
          overflow: 'hidden',
          flexShrink: 0,
          flexGrow: 0
        };
      } else {
        // Panel principal (plan) cuando está cerrado
        return {
          height: `100%`,
          width: size === '100%' ? '100%' : `${size}${unitMeasure}`,
          flex: '1 1 auto',
          overflow: 'hidden'
        };
      }
    }

    // Comportamiento original para modo resizable
    return {
      height: `100%`,
      width: `${size}${unitMeasure}`,
      overflow: 'hidden'
    };
  }

  startResize(e, index) {
    const { isToggleMode, isOpen } = this.state;
    // Permitir resize en modo toggle solo cuando está abierto
    if (isToggleMode && !isOpen) return;

    e.preventDefault();
    e.stopPropagation(); // Evitar que se active el toggle al hacer click
    this.hasResized = false; // Resetear el flag al iniciar resize

    // Flags síncronos: setState es async y si no, el primer mousemove
    // se pierde y el drag en columna (pocos empleados) se siente roto.
    const initialPos = this.displayDirectionIsColumn() ? e.clientY : e.clientX;
    this._resizing = true;
    this._currentPanel = index;
    this._initialPos = initialPos;
    this._panelsSize = Array.isArray(this.state.panelsSize)
      ? [...this.state.panelsSize]
      : [...(this.props.panelsSize || [])];

    this.setState({
      resizing: true,
      currentPanel: index,
      initialPos,
    });
  }

  executeResize = e => {
    if (!this._resizing) return;

    // Usar requestAnimationFrame para optimizar el rendimiento
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(() => {
      if (!this._resizing) return;

      const currentMousePosition = this.displayDirectionIsColumn()
        ? e.clientY
        : e.clientX;

      const displacement = this._initialPos - currentMousePosition;

      // Si el mouse se movió más de 3 píxeles, considerar que se hizo resize
      if (Math.abs(displacement) > 3) {
        this.hasResized = true;
      }

      const nextPanelsSize = this.getNextPanelsSize(
        displacement,
        this._panelsSize,
        this._currentPanel
      );

      this._panelsSize = nextPanelsSize;
      this._initialPos = currentMousePosition;

      this.setState({
        initialPos: currentMousePosition,
        panelsSize: nextPanelsSize,
        displacement,
        resizing: true,
        currentPanel: this._currentPanel,
      });
    });
  };

  stopResize = () => {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // El listener está en document: cualquier mouseup (la flecha de fechas,
    // un clic en la tabla) no debe reaplicar el último ancho arrastrado.
    if (!this._resizing) return;

    const wasResizing = this._resizing;
    this._resizing = false;

    const { isToggleMode } = this.state;
    const panelsSize = this._panelsSize || this.state.panelsSize;

    if (wasResizing && isToggleMode) {
      // Guardar el ancho del contador en localStorage solo si se hizo resize.
      // Arrastrar hasta el borde cierra el panel y no pisa el ancho elegido.
      if (this.hasResized) {
        const counterWidth = panelsSize[1];
        if (typeof counterWidth === 'string' && counterWidth.endsWith('px')) {
          const px = parseFloat(counterWidth);
          if (!Number.isFinite(px) || px <= 1) {
            localStorage.setItem("plan_panel_toggle", "false");
            this.setState({
              resizing: false,
              currentPanel: null,
              displacement: 0,
              isOpen: false,
              panelsSize: ['100%', '0'],
            }, () => this.notifyPanelsLayoutChange());
            setTimeout(() => {
              this.hasResized = false;
            }, 100);
            return;
          }
          localStorage.setItem("plan_counter_width", String(Math.round(px)));
        }
      }
    } else if (wasResizing && this.hasResized) {
      localStorage.setItem(this.getStorageKey(), panelsSize);
      if (typeof this.props.onResizeEnd === 'function') {
        this.props.onResizeEnd(panelsSize);
      }
    }

    this.setState({
      resizing: false,
      currentPanel: null,
      displacement: 0,
      panelsSize,
    });

    if (wasResizing && isToggleMode && this.hasResized) {
      this.notifyPanelsLayoutChange();
    }

    // Resetear el flag después de un pequeño delay para permitir que el click se procese
    setTimeout(() => {
      this.hasResized = false;
    }, 100);
  };

  forceResize = (panelsSize) => {
    this.setState({
      panelsSize,
    });
  };

  getCurrentComponentSize() {
    const componentSizes = this.resizable.current.getBoundingClientRect();

    return this.displayDirectionIsColumn()
      ? componentSizes.height
      : componentSizes.width;
  }

  getCurrentCounterPanelWidth() {
    const counterPanel = this.resizable.current?.querySelectorAll('.resizable-fragment')[1];
    if (!counterPanel) return null;
    return counterPanel.getBoundingClientRect().width;
  }

  getNextPanelsSize(displacement, panelsSizeOverride, currentPanelOverride) {
    const { isToggleMode, maxCounterWidth } = this.state;
    const currentPanelsSize = panelsSizeOverride || this.state.panelsSize;
    const currentPanel =
      currentPanelOverride !== undefined
        ? currentPanelOverride
        : this.state.currentPanel;

    // En modo toggle, trabajar directamente con píxeles
    if (isToggleMode && currentPanel === 1) {
      const minWidth = 0; // Permitir cerrar completamente con drag (100% -> 0%)
      const containerWidth = this.getCurrentComponentSize();
      const resizerWidth = parseFloat(this.props.resizerSize) || 10;
      // Tope por espacio disponible en el contenedor
      const containerLimit = Math.max(minWidth, containerWidth - resizerWidth);
      // El counter no debe crecer más que el ancho real de la tabla de contadores
      const intrinsicWidth = this.state.lastIntrinsicWidth;
      const contentLimit = intrinsicWidth > 0
        ? Math.max(intrinsicWidth + 2, MIN_COUNTER_PANEL_WIDTH)
        : containerLimit;
      const maxWidth = Math.min(containerLimit, contentLimit);

      // Obtener el ancho actual del contador en píxeles
      let currentWidth = 0;
      const currentSize = currentPanelsSize[1];
      if (typeof currentSize === 'string' && currentSize.endsWith('px')) {
        currentWidth = parseFloat(currentSize);
      } else if (currentSize === 'auto') {
        // Si está en auto, tomar el ancho real renderizado para evitar saltos iniciales
        const renderedWidth = this.getCurrentCounterPanelWidth();
        currentWidth = renderedWidth ?? (maxCounterWidth || 300);
      }

      // Calcular el nuevo ancho
      // displacement = initialPos - currentMousePosition
      // Si mueves el mouse a la izquierda: currentMousePosition < initialPos, displacement > 0
      // Queremos que mover a la izquierda agrande el contador (columna derecha)
      // Entonces: newWidth = currentWidth + displacement
      const newWidth = currentWidth + displacement;

      // Aplicar límites
      const clampedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

      return [currentPanelsSize[0], `${clampedWidth}px`];
    }

    // Comportamiento original para modo resizable normal
    const usePercentage = this.props.sizeUnitMeasure === '%';
    const resizeSize = usePercentage
      ? this.convertToPercentage(displacement)
      : displacement;

    const newPanelsSize = currentPanelsSize.map((panelSize, index) => {
      if (index === currentPanel) return Number(panelSize) + resizeSize;
      else if (index === currentPanel - 1)
        return Number(panelSize) - resizeSize;

      return Number(panelSize);
    });

    return this.clampPanelsSize(newPanelsSize);
  }

  convertToPercentage(displacement) {
    const size = this.getCurrentComponentSize();
    const resizerSize = parseFloat(this.props.resizerSize) || 10;
    // En columna el espacio útil excluye el resizer; si no, el drag
    // “pierde” píxeles y al fondo se siente trabado.
    const available = this.displayDirectionIsColumn()
      ? Math.max(1, size - resizerSize)
      : Math.max(1, size);

    return (displacement * 100) / available;
  }
}
